import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, Zap, ShieldCheck } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import { trackEvent } from "../lib/analytics";

/**
 * V232.5 — Compare Hub index page.
 *
 * Standalone `/compare` route that lists every TopRankPulse-vs-X page
 * we've shipped. Two SEO wins stacked:
 *   1. Owns the generic query "porovnanie seo nástrojov slovensko" /
 *      "best seo tool slovakia 2026".
 *   2. Feeds internal link juice to every leaf comparator page, which
 *      Google treats as a strong editorial endorsement signal.
 *
 * Static list for now (3 pages). When we ship 6+, this hub becomes
 * a real cornerstone piece worth H2 category grouping (CZ tools / SK
 * tools / plugins / agencies).
 */
const COMPARATORS = [
  {
    slug: "toprankpulse-vs-space-solutions",
    competitor: "SPACE Solutions",
    tagline: "Agentúrny PDF audit €790 vs self-serve €7",
    accent: "#a855f7",
    tldr: "€790 vs €7, 48h vs 10 min — vyhrali sme 11 z 15 funkcií",
    score: "11 výhier · 4 remízy",
  },
  {
    slug: "toprankpulse-vs-marketing-miner",
    competitor: "Marketing Miner",
    tagline: "AI SEO vs klasický keyword research + backlink tool",
    accent: "#f59e0b",
    tldr: "€49/mes vs €99/mes — vyhrali sme 10 z 16, MM silnejšie v klasickom SERP",
    score: "10 výhier · 4 remízy",
  },
  {
    slug: "toprankpulse-vs-collabim",
    competitor: "Collabim",
    tagline: "AI citation tracking vs SERP position tracker",
    accent: "#06b6d4",
    tldr: "€49/mes vs €59-149/mes — doplňujúce nástroje, nie konkurencia",
    score: "10 výhier · 3 remízy",
  },
  {
    slug: "toprankpulse-vs-semrush",
    competitor: "Semrush",
    tagline: "Lokálna AI SEO za €49 vs globálny giant za €220/mes",
    accent: "#ff642c",
    tldr: "€49/mes SK/CZ + AI vrstva vs €220/mes global SEO suite",
    score: "12 výhier · 1 remíza",
  },
  {
    slug: "toprankpulse-vs-mangools",
    competitor: "Mangools (KWFinder)",
    tagline: "Dva slovenské SEO nástroje — klasický vs AI-first",
    accent: "#ff6b35",
    tldr: "€49/mes AI SEO vs €29-79/mes klasický keyword suite (SK-born)",
    score: "10 výhier · 2 remízy",
  },
];

export default function CompareHubPage() {
  const canonical = "https://toprankpulse.com/compare";

  useEffect(() => {
    trackEvent("view_compare_hub", { page_type: "compare_hub" });
  }, []);

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "TopRankPulse vs. iné SEO nástroje — porovnania 2026",
    itemListElement: COMPARATORS.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://toprankpulse.com/compare/${c.slug}`,
      name: `TopRankPulse vs ${c.competitor}`,
    })),
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Porovnanie SEO nástrojov — TopRankPulse vs. konkurencia 2026</title>
        <meta
          name="description"
          content="Objektívne porovnania TopRankPulse s SPACE Solutions, Marketing Miner a Collabim. 15+ funkcií, cenníky k 02/2026, FAQ. Nájdi najlepší AI SEO nástroj pre SK/CZ firmu."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Porovnanie SEO nástrojov — TopRankPulse vs. konkurencia 2026" />
        <meta property="og:description" content="Objektívne porovnania s SPACE Solutions, Marketing Miner a Collabim. 15+ funkcií, cenníky k 02/2026." />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="sk_SK" />
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <Scale className="w-4 h-4" />
          <span>Porovnania SEO nástrojov · 2026</span>
        </div>

        <h1
          data-testid="hub-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs.</span>{" "}
          <span className="text-[#a855f7]">konkurencia</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            {COMPARATORS.length} nestranných porovnaní · aktualizované pravidelne
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="hub-intro">
          Hľadáš najlepší AI SEO nástroj pre SK/CZ firmu? Porovnali sme
          TopRankPulse so všetkými hlavnými hráčmi — agentúry, keyword research
          tools, SERP trackery. Žiadny marketing jazyk, len fakty, cenníky a
          side-by-side tabuľky.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="hub-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("hub_cta_click", { destination: "/snapshot" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="hub-cta-primary"
          >
            Preskočiť porovnania — audit za €7 <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <LiveCounters variant="dark" />
      </section>

      {/* COMPARATOR CARDS */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="hub-grid">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Vyber si porovnanie
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {COMPARATORS.map((c) => (
            <Link
              key={c.slug}
              to={`/compare/${c.slug}`}
              onClick={() => trackEvent("hub_card_click", { competitor: c.competitor })}
              className="group block rounded-xl border border-white/10 bg-white/5 hover:border-white/30 p-6 transition-colors"
              data-testid={`hub-card-${c.slug}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-xl font-black font-['Cabinet_Grotesk'] text-white group-hover:text-[#a855f7] transition-colors">
                  vs {c.competitor}
                </h3>
                <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-400/30 shrink-0">
                  {c.score}
                </span>
              </div>
              <p className="text-sm text-gray-300 mb-3 leading-snug">{c.tagline}</p>
              <p className="text-xs text-gray-400 leading-relaxed">{c.tldr}</p>
              <div className="mt-4 flex items-center gap-1 text-[13px] font-bold text-[#a855f7]">
                Prečítať porovnanie <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        <p className="text-xs text-gray-500 mt-6 text-center" data-testid="hub-disclaimer">
          Všetky porovnania sú vedené v súlade s EU Directive 2006/114/EC o
          klamlivej a porovnávacej reklame. Cenníky k 02/2026, overiteľné z
          verejných zdrojov. Chýba tvoj nástroj v porovnaní? Napíš nám.
        </p>
      </section>

      {/* VALUE / WHY-TRUST */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="hub-trust">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Prečo veriť týmto porovnaniam?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TrustCard
            icon={ShieldCheck}
            title="Overiteľné zdroje"
            body="Cenníky linkované na verejné cenové stránky konkurentov. Feature claims testované na free trial účtoch k 02/2026."
          />
          <TrustCard
            icon={Zap}
            title="Priznávame remízy aj straty"
            body="Keď má konkurent lepšiu funkciu, povieme to. Napr. Marketing Miner má 150M+ keyword DB — my ~5M. Žiadne falošné 'vyhrávame všetko'."
          />
          <TrustCard
            icon={Scale}
            title="EU-compliant"
            body="Directive 2006/114/EC vyžaduje objektívne porovnávanie na relevantných a verifikovateľných funkciách. To je presne čo robíme."
          />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="hub-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            Nerozhoduj sa tabuľkou. Spusti audit svojej domény.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Za <strong className="text-white">€7</strong> ti za 10 minút ukážeme kde ťa cituje
            ChatGPT, Perplexity, Gemini — a kde ťa vôbec nevidia. 14-dňová záruka vrátenia peňazí.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("hub_final_cta_click", { destination: "/snapshot" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="hub-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function TrustCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
